import * as React from "react";
import { inject, observer } from "mobx-react";
import { MobxComponent } from "../../../../../mobx/components/index";
import { withTranslation, WithTranslation } from "react-i18next";
import {
	DetailRowListItem,
	DetailRowList,
	OrderModalHeader,
	OrderTimeline,
	StatusButtonGrid,
	StatusButtonOption,
	ModalFieldLabel,
	ModalActionButton,
	ModalActionButtonRow,
	PrinterPickModal,
} from "@lib/components";
import { UI } from "../../../../../core/ui";
import { logger } from "@lib/common";
import { RestaurantItemModal } from "../../../common/item-modal";
import { action, observable } from "mobx";
import { Untrusive } from "@lib/common";
import cloneDeep from "lodash/cloneDeep";
import { nanoid } from "nanoid";
import _get from "lodash/get";
import _findIndex from "lodash/findIndex";
import styled from "styled-components";

const SectionTitle = styled.div`
	margin-bottom: 8px;
	font-size: 12px;
	font-weight: 500;
	line-height: 18px;
`;

type BookingStatus = T.Schema.Booking.BookingStatuses;

const STATUS_BUTTONS: Array<StatusButtonOption<BookingStatus>> = [
	{ value: "cancelled",   label: "Cancelled" },
	{ value: "unconfirmed", label: "Unconfirmed" },
	{ value: "confirmed",   label: "Confirmed" },
	{ value: "seated",      label: "Seated" },
	{ value: "complete",    label: "Complete" },
];


interface Props extends WithTranslation { }

@inject("store") @observer
export class RestaurantBookingModalClass extends MobxComponent<Props> {

	@observable tab: number = 0;
	/**
	 * Printer picker modal open (only when more than one printer is enabled).
	 * NOTE: must only be read inside this component's own render() — `renderLayout`
	 * runs inside RestaurantItemModal (not an observer), so reads there are not tracked.
	 */
	@observable printMenuOpen: boolean = false;

	@action setTab = (tab: number) => this.tab = tab;
	@action setPrintMenuOpen = (open: boolean) => this.printMenuOpen = open;

	enabledPrinters() {
		const r = this.injected.store.restaurant!;
		return (r.settings.printers || []).filter((p) => !p.disabled);
	}

	/** Sends the booking docket to one printer . */
	printBooking = async (item: T.Schema.Booking.BookingSchema, printer_id: string) => {
		const { store } = this.injected;
		try {
			Untrusive.start();
			const r = store.restaurant!;
			const type = "booking";
			const restaurant_id = r._id;
			const type_id = item._id;
			const job_id = nanoid();
			await store.api.print_receipt({ type, restaurant_id, type_id, printer_id, job_id });
			UI.notification.success("Booking sent to printer");
		}
		catch (e) {
			logger.captureException(e);
			UI.notification.error("An error occurred, try again soon or contact us", { timeout: 6000 });
		}
		finally {
			Untrusive.stop();
		}
	}

	/** Print button: one printer prints straight away, several open the picker. */
	handlePrintClick = (item: T.Schema.Booking.BookingSchema) => {
		const printers = this.enabledPrinters();
		if (printers.length === 0) return;
		if (printers.length === 1) {
			void this.printBooking(item, printers[0]._id);
			return;
		}
		this.setPrintMenuOpen(true);
	}

	query = async () => {
		try {
			const { store } = this.injected;
			const { query } = store.router.s;
			const item = store.booking;
			const queryButNoItem = query._id && !item;
			const queryItemMismatch = query._id && item && item._id !== query._id;
			if (queryButNoItem || queryItemMismatch) {

				// CHECK ORDERS COLLECTION FOR ORDER
				const booking = store.bookings.items.find((o) => o._id === query._id);
				if (booking) {
					store.setBooking(cloneDeep(booking));
					return;
				}

				// QUERY ORDER IF NOT IN COLLECTION
				const _id = query._id;
				const response = await store.api.booking_find({ _id });
				if (response.outcome) {
					store.router.push(`/restaurant/${store.restaurant!._id}/bookings`);
					store.setBooking(null);
					UI.notification.error(response.message);
				}
				else {
					store.setBooking(response.item);
				}

			}
		}
		catch (e) {
			logger.captureException(e);
			UI.notification.error("Error finding booking, please try again", { timeout: 5000 });
		}
	}

	close = () => {
		const { store } = this.injected;
		const r = store.restaurant!;
		this.setPrintMenuOpen(false);
		store.router.push(`/restaurant/${r._id}/bookings`);
		store.setBooking(null);
	}

	handleStatusSelect = async (item: T.Schema.Booking.BookingSchema, status: T.Schema.Booking.BookingStatuses) => {
		if (!status) return;
		await this.injected.store.service.booking.update_status(item._id, status);
	}

	handleDelete = async (item: T.Schema.Booking.BookingSchema) => {
		const { store } = this.injected;
		try {
			const proceed = confirm("Once deleted, it cannot be recovered. Delete the booking?");
			if (!proceed) return;
			Untrusive.start();
			const { _id } = item;
			const restaurant_id = store.restaurant!._id;
			const response = await store.api.booking_delete({ _id, restaurant_id });
			if (response.outcome) {
				UI.notification.error(response.message, { timeout: 6000 });
			} else {
				this.close();
				store.removeBooking(_id);
				UI.notification.success("Booking deleted");
			}
		} catch (e) {
			logger.captureException(e);
			UI.notification.error("An error occurred, try again soon or contact us", { timeout: 6000 });
		} finally {
			Untrusive.stop();
		}
	}

	renderDetails = (item: T.Schema.Booking.BookingSchema) => {
		const { t } = this.injected;

		let rows: DetailRowListItem[] = [
			{ l: "Booking For", v: t("datetimeFromTimestamp", { value: item.config.timestamp }) },
			{ l: "No. Of People", v: t("number", { value: item.config.number_of_people }) },
			{ l: "Placed", v: t("datetimeFromTimestamp", { value: item.created }) },
			{
				l: "Last Updated",
				h: !item.updated,
				v: item.updated ? t("datetimeFromTimestamp", { value: item.updated }) : "",
			},
			{ l: "Notes", noTranslate: true, v: item.notes },
			{ l: "Name", noTranslate: true, v: item.customer.name },
			{ l: "E-Mail", noTranslate: true, v: item.customer.email },
			{ l: "Phone", noTranslate: true, v: item.customer.phone },
		];

		const customFields: T.Schema.CustomFieldValue[] | undefined = _get(item, "custom_booking_field_list");
		if (customFields) {
			const customRows = customFields.map((field): DetailRowListItem => ({
				l: field.question.label,
				v: field.type === "checkbox" ? (field.answer === "yes" ? "✅" : "❌") : field.answer,
				noTranslate: true,
			}));
			const idx = _findIndex(rows, (r: DetailRowListItem) => r.l === "Notes");
			if (idx === -1) rows = [...rows, ...customRows];
			else rows.splice(idx + 1, 0, ...customRows);
		}

		return rows;
	}

	renderLayout = (item: T.Schema.Booking.BookingSchema) => {
		const { store } = this.injected;
		const canDelete = !!store.restrictions.restaurant.bookings_delete;
		const printers = this.enabledPrinters();

		return (
			<div>
				<OrderModalHeader
					title={<span className="notranslate">{item.customer.name}</span>}
					subtitle={item.customer.email ? <span className="notranslate">{item.customer.email}</span> : undefined}
					badge={`Booking No. ${item.number}`}
					onClose={this.close}
				/>

				<div style={{ display: "flex", flexDirection: "column", gap: 16, padding: 16 }}>
					<StatusButtonGrid<BookingStatus>
						label="Change Status"
						options={STATUS_BUTTONS}
						current={item.status}
						onSelect={(status) => this.handleStatusSelect(item, status)}
					/>

					{/* Actions: print (when printers are set up) + delete (permission-gated) */}
					{(printers.length > 0 || canDelete) && (
						<div>
							<ModalFieldLabel>Actions</ModalFieldLabel>
							<ModalActionButtonRow>
								{printers.length > 0 && (
									<ModalActionButton
										type="button"
										aria-haspopup={printers.length > 1 ? "dialog" : undefined}
										onClick={() => this.handlePrintClick(item)}
									>
										Print
									</ModalActionButton>
								)}
								{canDelete && (
									<ModalActionButton type="button" onClick={() => this.handleDelete(item)}>
										Delete Booking
									</ModalActionButton>
								)}
							</ModalActionButtonRow>
						</div>
					)}
				</div>

				<OrderTimeline
					status={item.status}
					isDelivery={false}
					isBooking={true}
				/>

				<div style={{ padding: "0 16px 16px 16px" }}>
					<SectionTitle>Booking Details</SectionTitle>
					<DetailRowList variant="figma" items={this.renderDetails(item)} />
				</div>
			</div>
		);
	}

	render() {

		const { store } = this.injected;
		const { query } = store.router.s;
		const _id = query._id || null;
		const item = store.booking;

		return (
			<div>
				<RestaurantItemModal<T.Schema.Booking.BookingSchema>
					active={!!_id}
					item={item}
					query={this.query}
					close={this.close}
					closeButton={false}>

					{(b) => this.renderLayout(b)}
				</RestaurantItemModal>

				{/* printer picker — rendered here (observer render) so printMenuOpen is tracked */}
				{item && (
					<PrinterPickModal
						active={this.printMenuOpen}
						close={() => this.setPrintMenuOpen(false)}
						title="Print Booking"
						badge={`Booking No. ${item.number}`}
						printers={this.enabledPrinters()}
						onPick={(printerId) => {
							this.setPrintMenuOpen(false);
							void this.printBooking(item, printerId);
						}}
					/>
				)}
			</div>
		);
	}

}

export const RestaurantBookingModal = withTranslation()(RestaurantBookingModalClass);
